import React, { useCallback, useEffect, useRef, useState } from 'react';
import { X, Square, Hexagon, Loader2, Maximize2, Minimize2, Lock, Move } from 'lucide-react';

/**
 * Boundary editor that works on the ACTUAL rendered figure (a live clone of the
 * iframe's #mapSvg), with draggable corner handles. The iframe is same-origin so
 * we read its SVG + __plotGeom directly. Corner drags reshape the boundary and
 * (on release) push new dimensions into the engine via SET_BOUNDARIES, then the
 * figure is re-snapshotted so it always shows the real drawing.
 */
interface Pt { x: number; y: number }
// vbX/vbY/vbW/vbH = the cropped viewBox (figure bbox) in SVG user coords.
interface Snap { canvasPts: Pt[]; scale: number; vbX: number; vbY: number; vbW: number; vbH: number; svg: SVGElement; roads: any; figCx: number; figCy: number; figW: number; figH: number }
type Shape = 'rectangle' | 'trapezoid';
interface Props {
  open: boolean;
  getIframe: () => HTMLIFrameElement | null;
  onClose: () => void;
}

const dist = (a: Pt, b: Pt) => Math.hypot(a.x - b.x, a.y - b.y);
// Snap to the nearest inch (1/12 ft) so feet+inches round-trip exactly.
// (Rounding to 0.1 ft = 1.2" would shift entered inches, e.g. 33'-3" -> 33'-4".)
const r1 = (x: number) => Math.round(x * 12) / 12;
// canvasPts order is [SW, SE, NE, NW]; the corner kept fixed is the dragged
// corner's opposite — map each index to a SET_BOUNDARIES anchor name.
const ANCHOR_FOR = ['bottom-left', 'bottom-right', 'top-right', 'top-left'];
const WINDOWED = { w: 680, h: 760 };

// feet per side from the 4 corners ([SW,SE,NE,NW])
const sidesFeet = (p: Pt[], sc: number) => ({
  n: dist(p[3], p[2]) / sc, // north / top   (NW-NE)
  e: dist(p[2], p[1]) / sc, // east  / right (NE-SE)
  s: dist(p[0], p[1]) / sc, // south / bottom(SW-SE)
  w: dist(p[3], p[0]) / sc, // west  / left  (NW-SW)
});

export const BoundaryFigureEditor: React.FC<Props> = ({ open, getIframe, onClose }) => {
  const figRef = useRef<HTMLDivElement>(null);
  const editRef = useRef<HTMLTextAreaElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const snapRef = useRef<Snap | null>(null);
  const didInitZoom = useRef(false);
  const [zoom, setZoom] = useState(1); // multiplier on the fit-to-box scale
  const [fullscreen, setFullscreen] = useState(false);
  const [isMobile, setIsMobile] = useState(() => typeof window !== 'undefined' && window.innerWidth <= 700);
  // Mobile: when locked, the canvas can't pan, so dragging handles moves only the
  // element (no accidental canvas movement). Unlock to pan/zoom the canvas.
  const [canvasLocked, setCanvasLocked] = useState(true);
  const [box, setBox] = useState(WINDOWED); // available canvas box (grows in fullscreen)
  const boxRef = useRef(box);
  useEffect(() => { boxRef.current = box; }, [box]);
  useEffect(() => {
    const calc = () => {
      const mob = window.innerWidth <= 700;
      setIsMobile(mob);
      // Mobile: canvas fills the width; the panel is a bottom sheet (~45vh), so the
      // canvas gets the rest of the height. Desktop: fixed window / fullscreen.
      setBox(mob
        ? { w: Math.max(240, window.innerWidth - 20), h: Math.max(220, Math.round(window.innerHeight * 0.55) - 44) }
        : fullscreen
          ? { w: Math.max(420, window.innerWidth - 300 - 96), h: Math.max(420, window.innerHeight - 170) }
          : WINDOWED);
    };
    calc();
    window.addEventListener('resize', calc);
    return () => window.removeEventListener('resize', calc);
  }, [fullscreen]);
  const [snap, setSnap] = useState<Snap | null>(null);
  const [pts, setPts] = useState<Pt[]>([]);
  const ptsRef = useRef<Pt[]>([]);
  const [shape, setShape] = useState<Shape>('rectangle');
  const shapeRef = useRef<Shape>('rectangle');
  const dragRef = useRef<number | null>(null);
  const origRef = useRef<{ n: number; s: number; e: number; w: number; shape: Shape } | null>(null);
  // Display px per SVG unit = the document's actual on-screen scale, captured
  // once at open so the popup figure is shown at the SAME size/proportions as
  // it appears in the document (relative ratios stay faithful).
  const kFixedRef = useRef<number>(0);
  const [dims, setDims] = useState<{ n: number; s: number; e: number; w: number }>({ n: 30, s: 30, e: 50, w: 50 });
  const [showName, setShowName] = useState(true);
  const [dimsOutside, setDimsOutside] = useState(false);
  const [roads, setRoads] = useState({ north: false, south: false, east: false, west: false });
  const [origin, setOrigin] = useState('SW'); // trapezoid fixed corner (right-angle axis)
  const [areaYd, setAreaYd] = useState('');
  const [areaMt, setAreaMt] = useState('');
  const [areaOverride, setAreaOverride] = useState(false);
  const [confirmRect, setConfirmRect] = useState(false); // guard trapezoid -> rectangle
  // In-popup element editing (move any text/block, double-click to edit text).
  // Nothing is selected by default — a click selects whatever is under it (the
  // plot shows reshape corners; a text/label/dimension shows a move outline).
  const [sel, setSel] = useState<{ left: number; top: number; width: number; height: number } | null>(null);
  const [selFig, setSelFig] = useState(false);
  const [selRoad, setSelRoad] = useState<string | null>(null); // selected road side (top/bottom/left/right)
  const [selPulse, setSelPulse] = useState<string | null>(null); // selected pulse key "side|0|1"
  const [editing, setEditing] = useState<{ field: string; left: number; top: number; eh: number; value: string; b: boolean; i: boolean; u: boolean; align: string; case: string; sc: boolean; ds: number; lh: number; fs: number } | null>(null);
  const [editPos, setEditPos] = useState<{ left: number; top: number } | null>(null); // dragged editor position (null = auto)
  const [note, setNote] = useState<string | null>(null);
  const noteTimer = useRef<any>(null);
  const flashNote = (msg: string) => { setNote(msg); if (noteTimer.current) clearTimeout(noteTimer.current); noteTimer.current = setTimeout(() => setNote(null), 2600); };

  const setPtsBoth = (next: Pt[]) => { ptsRef.current = next; setPts(next); };
  useEffect(() => { snapRef.current = snap; }, [snap]);
  // Scroll the canvas so the figure is centred at the given zoom.
  const centerOnFigure = (z: number) => {
    const sc = scrollRef.current; const s = snapRef.current; if (!sc || !s) return;
    const fitK = Math.min(boxRef.current.w / s.vbW, boxRef.current.h / s.vbH);
    const k = fitK * z;
    const cx = (s.figCx - s.vbX) * k + 18, cy = (s.figCy - s.vbY) * k + 18;
    sc.scrollLeft = Math.max(0, cx - sc.clientWidth / 2);
    sc.scrollTop = Math.max(0, cy - sc.clientHeight / 2);
  };
  const applyZoom = (z: number) => { const nz = Math.max(0.4, Math.min(6, Math.round(z * 100) / 100)); setZoom(nz); setTimeout(() => centerOnFigure(nz), 50); };
  const fitScreen = () => { setZoom(1); setTimeout(() => { const sc = scrollRef.current; if (sc) { sc.scrollLeft = 0; sc.scrollTop = 0; } }, 50); };
  // Re-frame the figure to the new canvas size when fullscreen/window changes.
  useEffect(() => {
    const s = snapRef.current; if (!s || !didInitZoom.current) return;
    const fitK = Math.min(box.w / s.vbW, box.h / s.vbH);
    const focusK = Math.min(box.w / s.figW, box.h / s.figH);
    const dz = Math.max(1, focusK / fitK);
    setZoom(dz);
    setTimeout(() => centerOnFigure(dz), 90);
  }, [box]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { shapeRef.current = shape; }, [shape]);

  const snapshot = useCallback(() => {
    const ifr = getIframe(); if (!ifr) return;
    const win: any = ifr.contentWindow; const doc = ifr.contentDocument; if (!win || !doc) return;
    const g = win.__plotGeom; const svgEl = doc.getElementById('mapSvg') as any;
    if (!g || !g.canvasPts || !svgEl) return;

    // Show the WHOLE document page (so the figure sits in real context), but
    // render only the figure: keep the plot, dimensions, road lines + the page
    // frame, and hide every other element (header, references, witnesses,
    // signatures, compass, watermark). Side/road labels are kept (data-plotfig).
    const vb = (svgEl.getAttribute('viewBox') || '0 0 793 1122').split(/\s+/).map(Number);
    const vbX = vb[0] || 0, vbY = vb[1] || 0, vbW = vb[2] || 793, vbH = vb[3] || 1122;

    const clone = svgEl.cloneNode(true) as SVGElement;
    clone.setAttribute('viewBox', `${vbX} ${vbY} ${vbW} ${vbH}`);
    const hide = (el: Element | null) => el && el.setAttribute('style', ((el.getAttribute('style') || '') + ';display:none'));
    hide(clone.querySelector('#layer-watermark'));
    const lt = clone.querySelector('#layer-text');
    if (lt) Array.prototype.forEach.call(lt.children, (ch: Element) => { if (!ch.hasAttribute('data-plotfig')) hide(ch); });
    // Make label hit-areas cover the whole text bbox (SVG text otherwise only
    // catches clicks on the painted glyphs — small rotated road labels get missed).
    Array.prototype.forEach.call(clone.querySelectorAll('[data-field]'), (el: Element) => {
      el.setAttribute('style', ((el.getAttribute('style') || '') + ';pointer-events:bounding-box'));
    });

    const cps: Pt[] = g.canvasPts.map((p: any) => ({ x: p.x, y: p.y }));
    // Figure bbox (plot quad) + generous margin → used to frame/zoom by default.
    const xs = cps.map((p) => p.x), ys = cps.map((p) => p.y);
    const fx0 = Math.min(...xs), fx1 = Math.max(...xs), fy0 = Math.min(...ys), fy1 = Math.max(...ys);
    const mx = (fx1 - fx0) * 0.6, my = (fy1 - fy0) * 0.6;
    const figW = (fx1 - fx0) + mx * 2, figH = (fy1 - fy0) + my * 2;
    const figCx = (fx0 + fx1) / 2, figCy = (fy0 + fy1) / 2;
    setSnap({ canvasPts: cps, scale: g.scale || 1, vbX, vbY, vbW, vbH, svg: clone, roads: g.roads || {}, figCx, figCy, figW, figH });
    setPtsBoth(cps);
    // Default zoom: frame the figure with margin (once per open).
    if (!didInitZoom.current) {
      didInitZoom.current = true;
      const fitK = Math.min(boxRef.current.w / vbW, boxRef.current.h / vbH);
      const focusK = Math.min(boxRef.current.w / figW, boxRef.current.h / figH);
      const dz = Math.max(1, focusK / fitK);
      setZoom(dz);
      setTimeout(() => centerOnFigure(dz), 60);
    }
  }, [getIframe]);

  // Undo/redo while the Plot editor is open: drive the engine history, then
  // re-snapshot so the popup reflects the restored plot.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      const t = e.target as HTMLElement | null;
      if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable)) return;
      const meta = e.ctrlKey || e.metaKey;
      let type: string | null = null;
      if (meta && (e.key === 'z' || e.key === 'Z')) type = e.shiftKey ? 'REDO' : 'UNDO';
      else if (meta && (e.key === 'y' || e.key === 'Y')) type = 'REDO';
      if (!type) return;
      e.preventDefault();
      getIframe()?.contentWindow?.postMessage({ type }, '*');
      setTimeout(snapshot, 160);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, getIframe, snapshot]);

  useEffect(() => {
    if (!open) { setSnap(null); didInitZoom.current = false; return; }
    didInitZoom.current = false;
    setFullscreen(false);
    const ifr = getIframe(); if (!ifr) return;
    const win: any = ifr.contentWindow; const doc = ifr.contentDocument;
    const val = (id: string) => parseFloat((doc?.getElementById(id) as HTMLInputElement | null)?.value || '') || 0;
    const ft = (f: string, i: string) => val(f) + val(i) / 12;
    const d = { n: ft('n_ft', 'n_in'), s: ft('s_ft', 's_in'), e: ft('e_ft', 'e_in'), w: ft('w_ft', 'w_in') };
    const sh: Shape = (Math.abs(d.n - d.s) > 0.05 || Math.abs(d.e - d.w) > 0.05) ? 'trapezoid' : 'rectangle';
    setShape(sh); shapeRef.current = sh;
    origRef.current = { ...d, shape: sh };
    // Read the current toggles from the engine's form.
    const chk = (id: string) => !!(doc?.getElementById(id) as HTMLInputElement | null)?.checked;
    setShowName(doc?.getElementById('show_plot_name') ? chk('show_plot_name') : true);
    setDimsOutside(chk('dims_outside'));
    setRoads({ north: chk('road_north'), south: chk('road_south'), east: chk('road_east'), west: chk('road_west') });
    setOrigin((doc?.getElementById('plot_origin') as HTMLSelectElement | null)?.value || 'SW');
    var ydEl = doc?.getElementById('area_sqyd_override') as HTMLInputElement | null;
    var mtEl = doc?.getElementById('area_sqm_override') as HTMLInputElement | null;
    var isOv = !!(ydEl && ydEl.dataset.manualOverride === '1') || !!(mtEl && mtEl.dataset.manualOverride === '1');
    setAreaOverride(isOv);
    setAreaYd(isOv && ydEl ? (ydEl.value || '') : '');
    setAreaMt(isOv && mtEl ? (mtEl.value || '') : '');
    setConfirmRect(false);
    setSel(null); setSelFig(false); setSelRoad(null); setSelPulse(null); setEditing(null);
    // Capture the document's actual on-screen scale (px per SVG unit) so the
    // popup shows the figure at exactly that size.
    const svgEl: any = doc?.getElementById('mapSvg');
    const ctm = svgEl?.getScreenCTM?.();
    kFixedRef.current = (ctm && ctm.a) || 0;
    void win;
    snapshot();
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  // Mount / replace the cloned figure inside the preview box.
  useEffect(() => {
    const c = figRef.current; if (!c) return;
    c.replaceChildren();
    if (snap) {
      const fit = Math.min(box.w / snap.vbW, box.h / snap.vbH);
      const k = fit * zoom;
      snap.svg.setAttribute('style', `width:${Math.round(snap.vbW * k)}px;height:${Math.round(snap.vbH * k)}px;display:block;background:#fff;overflow:hidden;`);
      c.appendChild(snap.svg);
    }
  }, [snap, zoom, box]);

  // Keep the dimension inputs in sync with the actual figure after each change.
  useEffect(() => {
    if (!snap) return;
    const f = sidesFeet(snap.canvasPts, snap.scale);
    setDims({ n: r1(f.n), s: r1(f.s), e: r1(f.e), w: r1(f.w) });
  }, [snap]);

  if (!open) return null;

  const fit = snap ? Math.min(box.w / snap.vbW, box.h / snap.vbH) : 1;
  const k = snap ? fit * zoom : 1;
  const dW = snap ? Math.round(snap.vbW * k) : box.w;
  const dH = snap ? Math.round(snap.vbH * k) : box.h;
  const toPx = (p: Pt) => ({ x: (p.x - (snap?.vbX || 0)) * k, y: (p.y - (snap?.vbY || 0)) * k });
  // Current (calculated or overridden) area values shown read-only when not editing.
  const _adoc = getIframe()?.contentDocument;
  const liveArea = {
    yd: (_adoc?.getElementById('area_sqyd_override') as HTMLInputElement | null)?.value || '',
    mt: (_adoc?.getElementById('area_sqm_override') as HTMLInputElement | null)?.value || '',
  };

  const applyToEngine = (next: Pt[], draggedIdx: number) => {
    if (!snap) return;
    const f = sidesFeet(next, snap.scale);
    const anchor = ANCHOR_FOR[(draggedIdx + 2) % 4];
    const ifr = getIframe(); const win = ifr?.contentWindow;
    win?.postMessage({ type: 'SET_BOUNDARIES', shape: shapeRef.current, n: r1(f.n), s: r1(f.s), e: r1(f.e), w: r1(f.w), anchor }, '*');
    setTimeout(snapshot, 80);
  };

  const startDrag = (e: React.PointerEvent, i: number) => {
    e.preventDefault(); e.stopPropagation();
    dragRef.current = i;
    const move = (ev: PointerEvent) => {
      const c = figRef.current; if (!c) return;
      const rect = c.getBoundingClientRect();
      const sx = (ev.clientX - rect.left) / k + (snap?.vbX || 0), sy = (ev.clientY - rect.top) / k + (snap?.vbY || 0);
      const prev = ptsRef.current;
      if (shapeRef.current === 'rectangle') {
        const opp = prev[(i + 2) % 4];
        const x0 = Math.min(opp.x, sx), x1 = Math.max(opp.x, sx), y0 = Math.min(opp.y, sy), y1 = Math.max(opp.y, sy);
        // order [SW, SE, NE, NW]
        setPtsBoth([{ x: x0, y: y1 }, { x: x1, y: y1 }, { x: x1, y: y0 }, { x: x0, y: y0 }]);
      } else {
        setPtsBoth(prev.map((p, idx) => idx === i ? { x: sx, y: sy } : p));
      }
    };
    const up = () => {
      window.removeEventListener('pointermove', move); window.removeEventListener('pointerup', up);
      const idx = dragRef.current; dragRef.current = null;
      if (idx != null) applyToEngine(ptsRef.current, idx);
    };
    window.addEventListener('pointermove', move); window.addEventListener('pointerup', up);
  };

  // ---- In-popup element editing: move any text/block + double-click to edit ----
  // Inverse of the element's screen-CTM linear part: maps a screen-pixel delta
  // into the element's own coordinate frame (so rotated text moves correctly and
  // the display scale is handled in one step).
  // Resolve the data-field element under a click — falls back to the inner field
  // of a data-plotfig label group (side/road labels) so the whole group works.
  const fieldElFrom = (t: Element): Element | null => {
    const direct = t.closest && t.closest('[data-field]');
    if (direct) return direct;
    const grp = t.closest && t.closest('[data-plotfig]');
    return grp ? grp.querySelector('[data-field]') : null;
  };
  // Robust fallback: SVG text only catches clicks on the painted glyphs, so a
  // thin/rotated label is easily missed. Find the smallest data-field whose
  // screen box contains the pointer.
  const pickField = (cx: number, cy: number): Element | null => {
    const cont = figRef.current; if (!cont) return null;
    let best: Element | null = null, bestArea = Infinity;
    cont.querySelectorAll('[data-field]').forEach((el) => {
      const r = el.getBoundingClientRect();
      if (!r.width && !r.height) return;
      if (cx >= r.left - 3 && cx <= r.right + 3 && cy >= r.top - 3 && cy <= r.bottom + 3) {
        const a = r.width * r.height;
        if (a < bestArea) { bestArea = a; best = el; }
      }
    });
    return best;
  };
  const invLinearOf = (el: Element | null) => {
    const m = (el as any)?.getScreenCTM?.();
    if (!m) return { a: 1 / k, b: 0, c: 0, d: 1 / k };
    const det = m.a * m.d - m.b * m.c || 1;
    return { a: m.d / det, b: -m.b / det, c: -m.c / det, d: m.a / det };
  };
  const setOffsetAbs = (kind: 'field' | 'block' | 'figure', payload: string, base: { x: number; y: number }, lx: number, ly: number) => {
    const win: any = getIframe()?.contentWindow; if (!win) return;
    if (kind === 'field') { const T = win.textOverrides || (win.textOverrides = {}); if (!T[payload]) T[payload] = { dx: 0, dy: 0, ds: 0, dw: 0 }; T[payload].dx = base.x + lx; T[payload].dy = base.y + ly; }
    else if (kind === 'block') { const key = payload === 'references' ? 'area' : payload; const B = win.blockOffsets || (win.blockOffsets = {}); if (!B[key]) B[key] = { x: 0, y: 0, s: 0 }; B[key].x = base.x + lx; B[key].y = base.y + ly; }
    else if (win.plotPosOffset) { win.plotPosOffset.x = base.x + lx; win.plotPosOffset.y = base.y + ly; }
    try { win.drawMap(); } catch (e) { /* ignore */ }
  };
  const onFigPointerDown = (e: React.PointerEvent) => {
    if (!snap) return;
    // Clicking the figure while an editor is open saves it first.
    if (editing) { commitEdit(editRef.current ? editRef.current.value : editing.value); }
    const win: any = getIframe()?.contentWindow; if (!win) return;
    const tEl = e.target as Element;
    // Pulse (V-mark) is its own element — select on click, drag to move.
    const puEl = tEl.closest && tEl.closest('[data-pulse]');
    if (puEl) {
      const key = puEl.getAttribute('data-pulse') || '';
      const sx = e.clientX, sy = e.clientY; let moved = false;
      const mv = (ev: PointerEvent) => { if (!moved && Math.abs(ev.clientX - sx) + Math.abs(ev.clientY - sy) < 3) return; moved = true; };
      const up2 = () => { window.removeEventListener('pointermove', mv); window.removeEventListener('pointerup', up2); if (!moved) { setSelPulse(key); setSel(null); setSelFig(false); setSelRoad(null); } };
      window.addEventListener('pointermove', mv); window.addEventListener('pointerup', up2);
      startPulseDrag(e, key, false);
      return;
    }
    const bEl = tEl.closest && tEl.closest('[data-block]');
    const fEl = fieldElFrom(tEl) || (!bEl ? pickField(e.clientX, e.clientY) : null);
    // Click anywhere on a road band (lines / between road & plot) selects it.
    if (!bEl && !fEl) {
      const rh = roadHitTest(e.clientX, e.clientY);
      if (rh) {
        const sx = e.clientX, sy = e.clientY; let moved = false;
        const mv = (ev: PointerEvent) => { if (!moved && Math.abs(ev.clientX - sx) + Math.abs(ev.clientY - sy) < 3) return; moved = true; };
        const up2 = () => { window.removeEventListener('pointermove', mv); window.removeEventListener('pointerup', up2); if (!moved) { setSelRoad(rh); setSel(null); setSelFig(false); setSelPulse(null); } };
        window.addEventListener('pointermove', mv); window.addEventListener('pointerup', up2);
        return;
      }
    }
    let kind: 'field' | 'block' | 'figure', payload = '', rectEl: Element | null = null;
    if (bEl) { kind = 'block'; payload = bEl.getAttribute('data-block') || ''; rectEl = bEl; }
    else if (fEl) { kind = 'field'; payload = fEl.getAttribute('data-field') || ''; rectEl = fEl; }
    else { kind = 'figure'; }
    const cont = figRef.current; if (!cont) return;
    const cr = cont.getBoundingClientRect();
    const selRect = rectEl ? (() => { const r = rectEl!.getBoundingClientRect(); return { left: r.left - cr.left, top: r.top - cr.top, width: r.width, height: r.height }; })() : null;
    // Capture base engine offset + the (constant) rotation/scale frame once.
    let base = { x: 0, y: 0 };
    if (kind === 'field') { const o = win.textOverrides?.[payload]; base = { x: o?.dx || 0, y: o?.dy || 0 }; }
    else if (kind === 'block') { const key = payload === 'references' ? 'area' : payload; const o = win.blockOffsets?.[key]; base = { x: o?.x || 0, y: o?.y || 0 }; }
    else { base = { x: win.plotPosOffset?.x || 0, y: win.plotPosOffset?.y || 0 }; }
    const inv = invLinearOf(rectEl || cont.querySelector('svg'));
    const sx = e.clientX, sy = e.clientY; let moved = false, raf = 0, ldx = 0, ldy = 0;
    const flush = () => { raf = 0; const lx = inv.a * ldx + inv.c * ldy, ly = inv.b * ldx + inv.d * ldy; setOffsetAbs(kind, payload, base, lx, ly); snapshot(); };
    const move = (ev: PointerEvent) => {
      ldx = ev.clientX - sx; ldy = ev.clientY - sy;
      if (!moved) { if (Math.abs(ldx) + Math.abs(ldy) < 3) return; moved = true; setSel(null); }
      if (!raf) raf = requestAnimationFrame(flush);
    };
    const roadSide = kind === 'field' ? roadSideForField(payload) : null;
    const isRoad = !!(roadSide && snap.roads && snap.roads[roadSide]);
    const up = () => {
      window.removeEventListener('pointermove', move); window.removeEventListener('pointerup', up);
      setSelPulse(null);
      if (moved) { if (raf) { cancelAnimationFrame(raf); flush(); } if (kind === 'figure') { setSelFig(true); setSelRoad(null); } else if (isRoad) setSelRoad(roadSide); }
      else if (kind === 'figure') { setSelFig(true); setSel(null); setSelRoad(null); }
      else if (isRoad) { setSelRoad(roadSide); setSel(null); setSelFig(false); }
      else if (selRect) { setSel(selRect); setSelFig(false); setSelRoad(null); }
    };
    window.addEventListener('pointermove', move); window.addEventListener('pointerup', up);
  };
  // Hit-test a click against each road's band (between near & far lines) so the
  // road can be selected by clicking anywhere on it, not just its label.
  const roadHitTest = (cx: number, cy: number): string | null => {
    if (!snap || !snap.roads) return null;
    const cont = figRef.current; if (!cont) return null;
    const r = cont.getBoundingClientRect();
    const px = (cx - r.left) / k + (snap.vbX || 0), py = (cy - r.top) / k + (snap.vbY || 0);
    const inPoly = (poly: Pt[]) => {
      let inside = false;
      for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
        const xi = poly[i].x, yi = poly[i].y, xj = poly[j].x, yj = poly[j].y;
        if (((yi > py) !== (yj > py)) && (px < (xj - xi) * (py - yi) / (yj - yi) + xi)) inside = !inside;
      }
      return inside;
    };
    for (const side of Object.keys(snap.roads)) {
      const rd = snap.roads[side]; if (!rd) continue;
      if (inPoly([rd.n1, rd.n2, rd.F1, rd.P, rd.F0])) return side;
    }
    return null;
  };
  // Map an editable side field to its road side (when that side has a road).
  const roadSideForField = (f: string): string | null => (
    f === 'north_txt' ? 'top' : f === 'south_txt' ? 'bottom' : f === 'east_txt' ? 'right' : f === 'west_txt' ? 'left' : null
  );
  // Ensure roadGeom[side] exists, seeded from the current geometry so partial
  // edits don't reset the road.
  const ensureRoad = (side: string) => {
    const win: any = getIframe()?.contentWindow; const rd = snap?.roads[side];
    const RG = win.roadGeom || (win.roadGeom = {});
    const r = RG[side] || (RG[side] = {});
    if (rd && r.width == null) r.width = rd.width;
    return r;
  };
  // Drag a road handle: width (far line out/in), end0/end1 (free — extend/rotate
  // each far segment to make an L), or pivot (move the off-centre corner).
  const startRoadDrag = (e: React.PointerEvent, side: string, kind: 'width' | 'end0' | 'end1' | 'pivot') => {
    e.preventDefault(); e.stopPropagation();
    const win: any = getIframe()?.contentWindow; if (!win || !snap) return;
    const rd = snap.roads[side]; if (!rd) return;
    const cf = ((win.roadGeom && win.roadGeom[side] && win.roadGeom[side].far) || {}) as any;
    const base = {
      width: rd.width, pdx: cf.pdx || 0, pdy: cf.pdy || 0,
      e0: { dx: (cf.e0 && cf.e0.dx) || 0, dy: (cf.e0 && cf.e0.dy) || 0 },
      e1: { dx: (cf.e1 && cf.e1.dx) || 0, dy: (cf.e1 && cf.e1.dy) || 0 },
    };
    const sx = e.clientX, sy = e.clientY; let raf = 0, ndx = 0, ndy = 0;
    const flush = () => {
      raf = 0;
      const svgDx = ndx / k, svgDy = ndy / k;
      const pN = svgDx * rd.outNorm.x + svgDy * rd.outNorm.y;
      const r = ensureRoad(side);
      const f = r.far || (r.far = { pdx: base.pdx, pdy: base.pdy, e0: { ...base.e0 }, e1: { ...base.e1 } });
      if (kind === 'width') r.width = Math.max(8, base.width + pN);
      else if (kind === 'end0') f.e0 = { dx: base.e0.dx + svgDx, dy: base.e0.dy + svgDy };
      else if (kind === 'end1') f.e1 = { dx: base.e1.dx + svgDx, dy: base.e1.dy + svgDy };
      else if (kind === 'pivot') { f.pdx = base.pdx + svgDx; f.pdy = base.pdy + svgDy; }
      try { win.drawMap(); } catch (err) { /* ignore */ }
      snapshot();
    };
    const move = (ev: PointerEvent) => { ndx = ev.clientX - sx; ndy = ev.clientY - sy; if (!raf) raf = requestAnimationFrame(flush); };
    const up = () => { window.removeEventListener('pointermove', move); window.removeEventListener('pointerup', up); if (raf) { cancelAnimationFrame(raf); flush(); } };
    window.addEventListener('pointermove', move); window.addEventListener('pointerup', up);
  };
  const removeBend = (side: string) => {
    const win: any = getIframe()?.contentWindow; if (!win || !snap) return;
    ensureRoad(side).far = { pdx: 0, pdy: 0, e0: { dx: 0, dy: 0 }, e1: { dx: 0, dy: 0 } };
    try { win.drawMap(); } catch (e) { /* ignore */ }
    snapshot();
  };
  // Drag a pulse to move it (default) or scale it (scale=true).
  const startPulseDrag = (e: React.PointerEvent, key: string, scale: boolean) => {
    e.preventDefault(); e.stopPropagation();
    const win: any = getIframe()?.contentWindow; if (!win || !snap) return;
    const [pside, pidx] = key.split('|'); const pk = 'pulse' + pidx;
    const r = ensureRoad(pside); const cur = r[pk] || {};
    const base = { dx: cur.dx || 0, dy: cur.dy || 0, s: cur.s || 1 };
    const sx = e.clientX, sy = e.clientY; let raf = 0, ndx = 0, ndy = 0;
    const flush = () => {
      raf = 0;
      const svgDx = ndx / k, svgDy = ndy / k;
      const rr = ensureRoad(pside);
      const keep = rr[pk] || {};
      if (scale) rr[pk] = { ...keep, dx: base.dx, dy: base.dy, s: Math.max(0.3, base.s + (svgDx + svgDy) / 60) };
      else rr[pk] = { ...keep, dx: base.dx + svgDx, dy: base.dy + svgDy, s: base.s };
      try { win.drawMap(); } catch (err) { /* ignore */ }
      snapshot();
    };
    const move = (ev: PointerEvent) => { ndx = ev.clientX - sx; ndy = ev.clientY - sy; if (!raf) raf = requestAnimationFrame(flush); };
    const up = () => { window.removeEventListener('pointermove', move); window.removeEventListener('pointerup', up); if (raf) { cancelAnimationFrame(raf); flush(); } };
    window.addEventListener('pointermove', move); window.addEventListener('pointerup', up);
  };
  // Rotate a pulse around its centre.
  const startPulseRotate = (e: React.PointerEvent, key: string) => {
    e.preventDefault(); e.stopPropagation();
    const win: any = getIframe()?.contentWindow; if (!win || !snap) return;
    const [pside, pidx] = key.split('|'); const pk = 'pulse' + pidx;
    const rd = snap.roads[pside]; const pu = rd && rd.pulses && rd.pulses.find((p: any) => p.key === key); if (!pu) return;
    const cont = figRef.current; if (!cont) return;
    const rect = cont.getBoundingClientRect();
    const cScreen = { x: rect.left + (pu.cx - (snap.vbX || 0)) * k, y: rect.top + (pu.cy - (snap.vbY || 0)) * k };
    const base = (ensureRoad(pside)[pk] || {}).r || 0;
    const startAng = Math.atan2(e.clientY - cScreen.y, e.clientX - cScreen.x);
    let raf = 0, ang = startAng;
    const flush = () => { raf = 0; const r = ensureRoad(pside); r[pk] = { ...(r[pk] || {}), r: base + (ang - startAng) * 180 / Math.PI }; try { win.drawMap(); } catch (e2) { /* ignore */ } snapshot(); };
    const move = (ev: PointerEvent) => { ang = Math.atan2(ev.clientY - cScreen.y, ev.clientX - cScreen.x); if (!raf) raf = requestAnimationFrame(flush); };
    const up = () => { window.removeEventListener('pointermove', move); window.removeEventListener('pointerup', up); if (raf) { cancelAnimationFrame(raf); flush(); } };
    window.addEventListener('pointermove', move); window.addEventListener('pointerup', up);
  };
  const setPulseFlag = (key: string, on: boolean) => {
    const win: any = getIframe()?.contentWindow; if (!win) return;
    const [pside, pidx] = key.split('|'); const r = ensureRoad(pside); const pk = 'pulse' + pidx;
    r[pk] = { ...(r[pk] || {}), on };
    try { win.drawMap(); } catch (e) { /* ignore */ }
    setSelPulse(on ? key : null);
    snapshot();
  };
  const onFigDoubleClick = (e: React.MouseEvent) => {
    const el = fieldElFrom(e.target as Element) || pickField(e.clientX, e.clientY);
    if (!el) return;
    const field = el.getAttribute('data-field') || '';
    // Dimensions are driven by the panel on the right — don't inline-edit them.
    if (/^dim_txt_/.test(field)) { setEditing(null); flashNote('Edit dimensions in the panel on the right →'); return; }
    const win: any = getIframe()?.contentWindow; const doc = getIframe()?.contentDocument; const cont = figRef.current; if (!doc || !cont) return;
    const inputEl = doc.getElementById(field) as HTMLInputElement | HTMLTextAreaElement | null;
    const cur = inputEl ? inputEl.value : (el.textContent || '');
    const ov = (win?.textOverrides && win.textOverrides[field]) || {};
    const r = el.getBoundingClientRect(); const cr = cont.getBoundingClientRect();
    setSel(null); setEditPos(null);
    setEditing({ field, left: r.left - cr.left, top: r.top - cr.top, eh: r.height, value: cur, b: !!ov.b, i: !!ov.i, u: !!ov.u, align: ov.align || '', case: ov.case || '', sc: !!ov.sc, ds: ov.ds || 0, lh: ov.lh || 1.2, fs: ov.fs || 0 });
  };
  // Apply the textarea content to the figure live as the user types.
  const liveEditApply = (val: string) => {
    if (!editing) return;
    const win: any = getIframe()?.contentWindow; const doc = getIframe()?.contentDocument;
    const inputEl = doc?.getElementById(editing.field) as HTMLInputElement | HTMLTextAreaElement | null;
    if (inputEl && win) { inputEl.value = val; try { win.drawMap(); } catch (e) { /* ignore */ } requestAnimationFrame(snapshot); }
  };
  // Apply a styling patch to the field, live, and reflect it in the toolbar.
  const setFieldStyle = (patch: Record<string, any>) => {
    const win: any = getIframe()?.contentWindow; if (!win || !editing) return;
    const T = win.textOverrides || (win.textOverrides = {});
    if (!T[editing.field]) T[editing.field] = { dx: 0, dy: 0, ds: 0, dw: 0 };
    Object.assign(T[editing.field], patch);
    try { win.drawMap(); } catch (e) { /* ignore */ }
    setEditing(ed => ed ? { ...ed, ...patch } as any : ed);
    requestAnimationFrame(snapshot);
  };
  const commitEdit = (val: string) => {
    if (!editing) return;
    const win: any = getIframe()?.contentWindow; const doc = getIframe()?.contentDocument;
    const inputEl = doc?.getElementById(editing.field) as HTMLInputElement | HTMLTextAreaElement | null;
    if (inputEl && win) {
      inputEl.value = val;
      // Persist (debounced save) AND redraw immediately so the next snapshot
      // captures the new text, not the previous frame.
      try { inputEl.dispatchEvent(new win.Event('input', { bubbles: true })); } catch (e) { /* ignore */ }
      try { win.drawMap(); } catch (e) { /* ignore */ }
    }
    setEditing(null);
    requestAnimationFrame(snapshot);
  };

  const applyDimsDirect = (d: { n: number; s: number; e: number; w: number }, anchor = 'top-left') => {
    const ifr = getIframe(); const win = ifr?.contentWindow;
    win?.postMessage({ type: 'SET_BOUNDARIES', shape: shapeRef.current, n: r1(d.n), s: r1(d.s), e: r1(d.e), w: r1(d.w), anchor }, '*');
    setTimeout(snapshot, 80);
  };
  // Set engine form fields (checkboxes) and redraw the figure.
  const setFields = (fields: Record<string, boolean>) => {
    const ifr = getIframe(); const win = ifr?.contentWindow;
    win?.postMessage({ type: 'SET_FIELDS', fields }, '*');
    setTimeout(snapshot, 80);
  };
  // Manual area override: set the input value + the engine's manual flag so it
  // isn't auto-recomputed (blank reverts to auto).
  const setArea = (id: string, val: string) => {
    const win: any = getIframe()?.contentWindow; const doc = getIframe()?.contentDocument;
    const el = doc?.getElementById(id) as HTMLInputElement | null;
    if (el) { el.value = val; el.dataset.manualOverride = val.trim() === '' ? '0' : '1'; }
    try { win.drawMap(); } catch (e) { /* ignore */ }
    setTimeout(snapshot, 60);
  };
  const toggleAreaOverride = () => {
    const doc = getIframe()?.contentDocument;
    const next = !areaOverride;
    setAreaOverride(next);
    if (next) {
      // Seed override fields with the current calculated values, then lock them.
      const yd = (doc?.getElementById('area_sqyd_override') as HTMLInputElement | null)?.value || '';
      const mt = (doc?.getElementById('area_sqm_override') as HTMLInputElement | null)?.value || '';
      setAreaYd(yd); setAreaMt(mt);
      setArea('area_sqyd_override', yd); setArea('area_sqm_override', mt);
    } else {
      setAreaYd(''); setAreaMt('');
      setArea('area_sqyd_override', ''); setArea('area_sqm_override', '');
    }
  };
  const toggleName = () => { const v = !showName; setShowName(v); setFields({ show_plot_name: v }); };
  const toggleDimsOutside = (v: boolean) => { setDimsOutside(v); setFields({ dims_outside: v }); };
  const toggleRoad = (side: 'north' | 'south' | 'east' | 'west') => { const v = !roads[side]; setRoads(r => ({ ...r, [side]: v })); setFields({ ['road_' + side]: v }); };
  const commitSide = (key: 'n' | 's' | 'e' | 'w', val: number) => {
    const v = Math.max(1, val || 0);
    const d = { ...dims };
    if (shape === 'rectangle') { if (key === 'n' || key === 's') { d.n = v; d.s = v; } else { d.e = v; d.w = v; } }
    else d[key] = v;
    setDims(d);
    applyDimsDirect(d, 'top-left');
  };

  const doConvertShape = (sh: Shape) => { setShape(sh); shapeRef.current = sh; setConfirmRect(false); applyDimsDirect(dims, 'center'); };
  const toggleShape = (sh: Shape) => {
    if (sh === shape) return;
    // Trapezoid -> rectangle flattens the shape: confirm first (don't lose work).
    if (shape === 'trapezoid' && sh === 'rectangle') { setConfirmRect(true); return; }
    doConvertShape(sh);
  };
  const cancel = () => {
    const o = origRef.current; const ifr = getIframe(); const win = ifr?.contentWindow;
    if (o && win) win.postMessage({ type: 'SET_BOUNDARIES', shape: o.shape, n: r1(o.n), s: r1(o.s), e: r1(o.e), w: r1(o.w), anchor: 'center' }, '*');
    onClose();
  };

  const ptsPx = pts.map(toPx);
  const path = ptsPx.length ? ptsPx.map((p, i) => `${i ? 'L' : 'M'}${p.x.toFixed(1)} ${p.y.toFixed(1)}`).join(' ') + ' Z' : '';
  const seg = (active: boolean): React.CSSProperties => ({
    display: 'flex', alignItems: 'center', gap: 6, background: active ? '#FFD93D' : '#1a1a1a',
    color: active ? '#1a1a1a' : '#bbb', border: '1px solid ' + (active ? '#FFD93D' : '#333'),
    borderRadius: 7, padding: '7px 12px', fontSize: 12, fontWeight: 600, cursor: 'pointer',
  });

  const zoomBtn: React.CSSProperties = { width: 24, height: 24, borderRadius: 6, border: '1px solid #3a3a3a', background: '#1f1f1f', color: '#ddd', cursor: 'pointer', fontSize: 14, lineHeight: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 };
  // Split a decimal-feet value into whole feet + inches (0–11), carrying 12" up.
  const splitFtIn = (v: number) => {
    let ft = Math.floor(Math.max(0, v));
    let inch = Math.round((Math.max(0, v) - ft) * 12);
    if (inch >= 12) { ft += 1; inch = 0; }
    return { ft, inch };
  };
  // Feet + inches input per side (like the old editor's n_ft / n_in fields).
  const numInput = (label: string, key: 'n' | 's' | 'e' | 'w', value: number) => {
    const { ft, inch } = splitFtIn(value);
    const toDec = (f: number, i: number) => Math.max(0, f) + Math.min(11, Math.max(0, i)) / 12;
    const live = (dec: number) => setDims(d => ({ ...d, [key]: dec, ...(shape === 'rectangle' ? (key === 'n' ? { s: dec } : { w: dec }) : {}) }));
    // One bordered "shell" per side groups ft + in as a single compact block; the
    // inputs are borderless inside it, split by a hairline divider. Values are
    // small (ft ≤ 3 digits, in ≤ 2), so the fields are sized tight to fit.
    const shell: React.CSSProperties = { display: 'inline-flex', alignSelf: 'flex-start', alignItems: 'stretch', background: '#1a1a1a', border: '1px solid #333', borderRadius: 8, overflow: 'hidden' };
    const cell: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: 3, padding: '2px 8px' };
    const inner = (w: number): React.CSSProperties => ({ width: w, minWidth: 0, background: 'transparent', border: 'none', color: '#fff', padding: '6px 0', fontSize: 13, outline: 'none', textAlign: 'right' });
    const unit: React.CSSProperties = { color: '#777', fontSize: 11, flexShrink: 0 };
    return (
      <label style={{ display: 'flex', flexDirection: 'column', gap: 4, flex: 1 }}>
        <span style={{ color: '#aaa', fontSize: 11 }}>{label}</span>
        <div style={shell}>
          <div style={cell}>
            <input type="number" min={0} value={ft} aria-label={`${label} feet`} className="no-spin"
              onChange={ev => live(toDec(parseFloat(ev.target.value) || 0, inch))}
              onBlur={ev => commitSide(key, toDec(parseFloat(ev.target.value) || 0, inch))}
              onKeyDown={ev => { if (ev.key === 'Enter') (ev.target as HTMLInputElement).blur(); }}
              style={inner(30)} />
            <span style={unit}>ft</span>
          </div>
          <span style={{ width: 1, alignSelf: 'stretch', background: '#333', flexShrink: 0 }} />
          <div style={cell}>
            <input type="number" min={0} max={11} value={inch} aria-label={`${label} inches`} className="no-spin"
              onChange={ev => live(toDec(ft, parseFloat(ev.target.value) || 0))}
              onBlur={ev => commitSide(key, toDec(ft, parseFloat(ev.target.value) || 0))}
              onKeyDown={ev => { if (ev.key === 'Enter') (ev.target as HTMLInputElement).blur(); }}
              style={inner(20)} />
            <span style={unit}>in</span>
          </div>
        </div>
      </label>
    );
  };

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 410, background: 'rgba(0,0,0,0.7)', display: 'flex' }}>
      <div style={{ margin: isMobile ? 0 : 'auto',
        width: isMobile ? '100vw' : (fullscreen ? '98vw' : 1060), maxWidth: isMobile ? 'none' : (fullscreen ? 'none' : '97vw'),
        height: isMobile ? '100dvh' : (fullscreen ? '96vh' : 820), maxHeight: isMobile ? 'none' : (fullscreen ? 'none' : '95vh'),
        display: 'flex', flexDirection: 'column', background: '#161616', borderRadius: isMobile ? 0 : (fullscreen ? 8 : 14), border: isMobile ? 'none' : '1px solid #2a2a2a', overflow: 'hidden' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 18px', borderBottom: '1px solid #222', flexShrink: 0, gap: 16 }}>
          <div style={{ color: '#FFD93D', fontSize: 16, fontWeight: 800 }}>Plot</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            {!isMobile && (
              <button onClick={() => setFullscreen((v) => !v)} title={fullscreen ? 'Exit full width' : 'Full width'}
                style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'none', border: '1px solid #333', borderRadius: 6, color: '#bbb', cursor: 'pointer', padding: '5px 9px', fontSize: 12 }}>
                {fullscreen ? <><Minimize2 size={13} /> Exit</> : <><Maximize2 size={13} /> Full width</>}
              </button>
            )}
            <button onClick={cancel} style={{ background: 'none', border: 'none', color: '#888', cursor: 'pointer', padding: 4 }}><X size={20} /></button>
          </div>
        </div>

        {/* Two columns on desktop; stacked (canvas over panel) on mobile. */}
        <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: 0, minHeight: 0, flex: 1 }}>
          {/* LEFT: actual figure canvas */}
          <div style={{ position: 'relative', flex: 1, minWidth: 0, minHeight: 0, display: 'flex' }}>
          {/* Mobile canvas lock toggle — freezes panning so element drags don't move the canvas. */}
          {isMobile && (
            <button onClick={() => setCanvasLocked((v) => !v)}
              title={canvasLocked ? 'Canvas locked — tap to allow panning' : 'Canvas movable — tap to lock for editing'}
              style={{ position: 'absolute', top: 8, left: 8, zIndex: 7, display: 'flex', alignItems: 'center', gap: 5,
                background: canvasLocked ? '#FFD93D' : 'rgba(13,13,13,.9)', color: canvasLocked ? '#1a1a1a' : '#ddd',
                border: '1px solid ' + (canvasLocked ? '#FFD93D' : '#3a3a3a'), borderRadius: 8, padding: '6px 9px', fontSize: 11, fontWeight: 700, cursor: 'pointer' }}>
              {canvasLocked ? <><Lock size={13} /> Editing</> : <><Move size={13} /> Pan</>}
            </button>
          )}
          <div ref={scrollRef} style={{ position: 'relative', padding: 18, overflow: (isMobile && canvasLocked) ? 'hidden' : 'auto', touchAction: (isMobile && canvasLocked) ? 'none' : 'auto', display: 'flex', alignItems: 'safe center', justifyContent: 'safe center', background: '#0e0e0e', flex: 1, minWidth: 0 }}>
            <div style={{ position: 'relative', width: dW, height: dH, flexShrink: 0 }}>
              {/* Clone of the actual figure — click any element to select, drag to
                  move, double-click text to edit. */}
              <div ref={figRef} onPointerDown={onFigPointerDown} onDoubleClick={onFigDoubleClick}
                style={{ position: 'absolute', inset: 0, borderRadius: 6, overflow: 'hidden', boxShadow: '0 2px 18px rgba(0,0,0,0.5)', touchAction: 'none', cursor: 'move' }} />
              {/* Light reference grid (over the figure, below the handles). */}
              {snap && (
                <div style={{
                  position: 'absolute', inset: 0, pointerEvents: 'none', borderRadius: 6,
                  backgroundImage: 'linear-gradient(rgba(45,156,219,.18) 1px, transparent 1px), linear-gradient(90deg, rgba(45,156,219,.18) 1px, transparent 1px), linear-gradient(rgba(45,156,219,.08) 1px, transparent 1px), linear-gradient(90deg, rgba(45,156,219,.08) 1px, transparent 1px)',
                  backgroundSize: '80px 80px, 80px 80px, 16px 16px, 16px 16px',
                }} />
              )}
              {!snap && (
                <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Loader2 size={28} className="animate-spin" style={{ color: '#FFD93D' }} />
                </div>
              )}
              {/* Selected-element outline (text / label / dimension) */}
              {sel && (
                <div style={{ position: 'absolute', left: sel.left - 3, top: sel.top - 3, width: sel.width + 6, height: sel.height + 6, border: '1.5px solid #2f7bff', borderRadius: 3, pointerEvents: 'none' }} />
              )}
              {/* Boundary path + reshape corners — only when the plot is selected */}
              {snap && selFig && (
                <svg width={dW} height={dH} style={{ position: 'absolute', inset: 0, touchAction: 'none', pointerEvents: 'none' }}>
                  <path d={path} fill="rgba(76,144,255,0.10)" stroke="#2f7bff" strokeWidth={2} strokeDasharray="6,4" strokeLinejoin="round" />
                  {ptsPx.map((p, i) => (
                    <circle key={i} cx={p.x} cy={p.y} r={6} fill="transparent" stroke="#2f7bff" strokeWidth={2.5}
                      style={{ cursor: 'grab', pointerEvents: 'all' }} onPointerDown={(e) => startDrag(e, i)} />
                  ))}
                </svg>
              )}
              {/* Road handles for the SELECTED road — width (orange), end dots
                  (blue), pivot (yellow), pulses (purple shown / green + to add). */}
              {snap && selRoad && snap.roads && snap.roads[selRoad] && (() => {
                const rd = snap.roads[selRoad]; if (!rd) return null;
                const widH = toPx({ x: (rd.fb1.x + rd.fb2.x) / 2, y: (rd.fb1.y + rd.fb2.y) / 2 });
                const f0 = toPx(rd.F0), f1 = toPx(rd.F1), pv = toPx(rd.P);
                const H = (x: number, y: number, color: string, on: any, dbl?: any, r = 5) => (
                  <circle cx={x} cy={y} r={r} fill="transparent" stroke={color} strokeWidth={2.5}
                    style={{ cursor: 'grab', pointerEvents: 'all' }} onPointerDown={on} onDoubleClick={dbl} />
                );
                return (
                  <svg width={dW} height={dH} style={{ position: 'absolute', inset: 0, pointerEvents: 'none', overflow: 'visible' }}>
                    {H(f0.x, f0.y, '#2f7bff', (e: any) => startRoadDrag(e, selRoad, 'end0'))}
                    {H(f1.x, f1.y, '#2f7bff', (e: any) => startRoadDrag(e, selRoad, 'end1'))}
                    {H(widH.x, widH.y, '#e67e22', (e: any) => startRoadDrag(e, selRoad, 'width'))}
                    {H(pv.x, pv.y, '#FFD93D', (e: any) => startRoadDrag(e, selRoad, 'pivot'), () => removeBend(selRoad))}
                    {(rd.pulses || []).map((pu: any) => {
                      const c = toPx({ x: pu.cx, y: pu.cy });
                      if (pu.shown) return (
                        <circle key={pu.key} cx={c.x} cy={c.y} r={6} fill="transparent" stroke="#9b59b6" strokeWidth={2.5}
                          style={{ cursor: 'grab', pointerEvents: 'all' }}
                          onPointerDown={(e) => { e.stopPropagation(); setSelPulse(pu.key); startPulseDrag(e, pu.key, false); }}
                          onDoubleClick={() => setPulseFlag(pu.key, false)} />
                      );
                      return (
                        <g key={pu.key} style={{ cursor: 'pointer', pointerEvents: 'all' }}
                          onPointerDown={(e) => { e.preventDefault(); e.stopPropagation(); setPulseFlag(pu.key, true); }}>
                          <circle cx={c.x} cy={c.y} r={7} fill="rgba(46,204,113,.15)" stroke="#2ecc71" strokeWidth={2} strokeDasharray="3,2" />
                          <line x1={c.x - 3} y1={c.y} x2={c.x + 3} y2={c.y} stroke="#2ecc71" strokeWidth={1.5} />
                          <line x1={c.x} y1={c.y - 3} x2={c.x} y2={c.y + 3} stroke="#2ecc71" strokeWidth={1.5} />
                        </g>
                      );
                    })}
                  </svg>
                );
              })()}
              {/* Selected pulse — small dot to move, ring to scale/extend, dbl-click to delete. */}
              {snap && selPulse && (() => {
                const [pside] = selPulse.split('|');
                const rd = snap.roads && snap.roads[pside]; if (!rd || !rd.pulses) return null;
                const pu = rd.pulses.find((p: any) => p.key === selPulse); if (!pu || !pu.shown) return null;
                const c = toPx({ x: pu.cx, y: pu.cy });
                return (
                  <svg width={dW} height={dH} style={{ position: 'absolute', inset: 0, pointerEvents: 'none', overflow: 'visible' }}>
                    <circle cx={c.x} cy={c.y} r={22} fill="none" stroke="#e67e22" strokeWidth={2} strokeDasharray="4,3"
                      style={{ cursor: 'nwse-resize', pointerEvents: 'all' }} onPointerDown={(e) => startPulseDrag(e, selPulse, true)} />
                    {/* rotate grip */}
                    <line x1={c.x} y1={c.y} x2={c.x} y2={c.y - 34} stroke="#16a085" strokeWidth={1.5} />
                    <circle cx={c.x} cy={c.y - 34} r={5} fill="transparent" stroke="#16a085" strokeWidth={2.5}
                      style={{ cursor: 'grab', pointerEvents: 'all' }} onPointerDown={(e) => startPulseRotate(e, selPulse)} />
                    <circle cx={c.x} cy={c.y} r={5} fill="transparent" stroke="#9b59b6" strokeWidth={2.5}
                      style={{ cursor: 'grab', pointerEvents: 'all' }} onPointerDown={(e) => startPulseDrag(e, selPulse, false)}
                      onDoubleClick={() => setPulseFlag(selPulse, false)} />
                  </svg>
                );
              })()}
              {/* Inline rich-text editor: formatting toolbar + multi-line textarea
                  (Enter = line break) + a Save button. */}
              {editing && (() => {
                const taRef = editRef;
                const tbtn = (active: boolean): React.CSSProperties => ({
                  minWidth: 26, height: 26, padding: '0 6px', borderRadius: 5, cursor: 'pointer', fontSize: 12, lineHeight: 1,
                  background: active ? '#FFD93D' : '#1f1f1f', color: active ? '#1a1a1a' : '#ddd', border: '1px solid ' + (active ? '#FFD93D' : '#3a3a3a'),
                });
                const PANEL_H = 220, PANEL_W = 388;
                const below = editing.top + editing.eh + 10;
                const autoTop = (below + PANEL_H <= dH) ? below : Math.max(2, editing.top - PANEL_H - 10);
                const autoLeft = Math.max(2, Math.min(editing.left - 10, dW - PANEL_W));
                const panelLeft = editPos ? editPos.left : autoLeft;
                const panelTop = editPos ? editPos.top : autoTop;
                const startPanelDrag = (ev: React.PointerEvent) => {
                  ev.preventDefault(); ev.stopPropagation();
                  const sx = ev.clientX, sy = ev.clientY, bx = panelLeft, by = panelTop;
                  const mv = (e: PointerEvent) => setEditPos({ left: bx + (e.clientX - sx), top: by + (e.clientY - sy) });
                  const u2 = () => { window.removeEventListener('pointermove', mv); window.removeEventListener('pointerup', u2); };
                  window.addEventListener('pointermove', mv); window.addEventListener('pointerup', u2);
                };
                return (
                  <div style={{ position: 'absolute', left: panelLeft, top: panelTop, width: PANEL_W, boxSizing: 'border-box', zIndex: 6, background: '#0d0d0d', border: '1px solid #2a2a2a', borderRadius: 10, padding: 12, boxShadow: '0 8px 24px rgba(0,0,0,.55)' }}
                    onPointerDown={(e) => e.stopPropagation()}>
                    <div onPointerDown={startPanelDrag} style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'move', marginBottom: 8, color: '#888', fontSize: 11, fontWeight: 600, userSelect: 'none' }}>
                      <span style={{ letterSpacing: 1 }}>⠿</span> Edit text — drag to move
                    </div>
                    <div style={{ display: 'flex', gap: 4, marginBottom: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                      <button style={{ ...tbtn(editing.b), fontWeight: 800 }} title="Bold" onMouseDown={(e) => { e.preventDefault(); setFieldStyle({ b: !editing.b }); }}>B</button>
                      <button style={{ ...tbtn(editing.i), fontStyle: 'italic' }} title="Italic" onMouseDown={(e) => { e.preventDefault(); setFieldStyle({ i: !editing.i }); }}>I</button>
                      <button style={{ ...tbtn(editing.u), textDecoration: 'underline' }} title="Underline" onMouseDown={(e) => { e.preventDefault(); setFieldStyle({ u: !editing.u }); }}>U</button>
                      <span style={{ width: 1, height: 18, background: '#333', margin: '0 2px' }} />
                      <button style={tbtn(editing.align === 'start')} title="Align left" onMouseDown={(e) => { e.preventDefault(); setFieldStyle({ align: 'start' }); }}>L</button>
                      <button style={tbtn(editing.align === 'middle' || !editing.align)} title="Align center" onMouseDown={(e) => { e.preventDefault(); setFieldStyle({ align: 'middle' }); }}>C</button>
                      <button style={tbtn(editing.align === 'end')} title="Align right" onMouseDown={(e) => { e.preventDefault(); setFieldStyle({ align: 'end' }); }}>R</button>
                      <span style={{ width: 1, height: 18, background: '#333', margin: '0 2px' }} />
                      <button style={tbtn(editing.case === 'upper')} title="ALL CAPS" onMouseDown={(e) => { e.preventDefault(); setFieldStyle({ case: editing.case === 'upper' ? '' : 'upper' }); }}>AA</button>
                      <button style={tbtn(editing.case === 'lower')} title="lowercase" onMouseDown={(e) => { e.preventDefault(); setFieldStyle({ case: editing.case === 'lower' ? '' : 'lower' }); }}>aa</button>
                      <button style={tbtn(editing.case === 'title')} title="Title Case" onMouseDown={(e) => { e.preventDefault(); setFieldStyle({ case: editing.case === 'title' ? '' : 'title' }); }}>Aa</button>
                      <button style={tbtn(editing.sc)} title="Small caps" onMouseDown={(e) => { e.preventDefault(); setFieldStyle({ sc: !editing.sc }); }}>S&#x1D04;</button>
                    </div>
                    <div style={{ display: 'flex', gap: 4, marginBottom: 8, alignItems: 'center' }}>
                      <span style={{ color: '#888', fontSize: 10, marginRight: 2 }}>Size</span>
                      <select value={editing.fs || ''} onChange={(e) => setFieldStyle({ fs: e.target.value ? parseInt(e.target.value, 10) : 0 })}
                        style={{ background: '#1f1f1f', color: '#ddd', border: '1px solid #3a3a3a', borderRadius: 5, padding: '4px 6px', fontSize: 12, outline: 'none', cursor: 'pointer' }}>
                        <option value="">Auto</option>
                        {Array.from({ length: 13 }, (_, n) => 8 + n).map((px) => <option key={px} value={px}>{px}px</option>)}
                      </select>
                      <span style={{ width: 1, height: 18, background: '#333', margin: '0 2px' }} />
                      <span style={{ color: '#888', fontSize: 10, marginRight: 2 }}>Line</span>
                      <button style={tbtn(false)} title="Tighter lines" onMouseDown={(e) => { e.preventDefault(); setFieldStyle({ lh: Math.max(0.8, Math.round(((editing.lh || 1.2) - 0.1) * 10) / 10) }); }}>&minus;</button>
                      <button style={tbtn(false)} title="Looser lines" onMouseDown={(e) => { e.preventDefault(); setFieldStyle({ lh: Math.min(2.5, Math.round(((editing.lh || 1.2) + 0.1) * 10) / 10) }); }}>+</button>
                      <span style={{ color: '#777', fontSize: 10, minWidth: 26, textAlign: 'center' }}>{(editing.lh || 1.2).toFixed(1)}</span>
                    </div>
                    <textarea ref={taRef} autoFocus defaultValue={editing.value} rows={Math.min(6, Math.max(2, (editing.value.match(/\n/g) || []).length + 2))}
                      onChange={(e) => liveEditApply(e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Escape') { e.preventDefault(); setEditing(null); } }}
                      style={{ display: 'block', width: '100%', boxSizing: 'border-box', background: '#161616', color: '#fff', border: '1px solid #2a2a2a', borderRadius: 6, padding: '8px 9px', fontSize: 13, resize: 'vertical', fontFamily: 'inherit', lineHeight: 1.3, outline: 'none' }} />
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 8 }}>
                      <span style={{ color: '#777', fontSize: 10 }}>Enter = new line</span>
                      <div style={{ display: 'flex', gap: 6 }}>
                        <button onClick={() => setEditing(null)} style={{ padding: '5px 10px', borderRadius: 6, border: '1px solid #3a3a3a', background: 'transparent', color: '#aaa', cursor: 'pointer', fontSize: 12 }}>Cancel</button>
                        <button onClick={() => commitEdit(taRef.current ? taRef.current.value : editing.value)} style={{ padding: '5px 14px', borderRadius: 6, border: 'none', background: '#FFD93D', color: '#1a1a1a', cursor: 'pointer', fontSize: 12, fontWeight: 700 }}>Save</button>
                      </div>
                    </div>
                  </div>
                );
              })()}
              {note && (
                <div style={{ position: 'absolute', left: '50%', top: 10, transform: 'translateX(-50%)', zIndex: 7, background: 'rgba(13,13,13,.96)', color: '#FFD93D', border: '1px solid #2f7bff', borderRadius: 8, padding: '8px 14px', fontSize: 12, fontWeight: 600, whiteSpace: 'nowrap', boxShadow: '0 4px 16px rgba(0,0,0,.5)', pointerEvents: 'none' }}>{note}</div>
              )}
            </div>
          </div>
          {/* Zoom controls (floating over the canvas) */}
          {snap && (
            <div style={{ position: 'absolute', left: '50%', bottom: 12, transform: 'translateX(-50%)', zIndex: 8, display: 'flex', alignItems: 'center', gap: 8, background: 'rgba(13,13,13,.94)', border: '1px solid #2a2a2a', borderRadius: 10, padding: '6px 10px', boxShadow: '0 6px 20px rgba(0,0,0,.5)' }}>
              <button onClick={() => applyZoom(zoom * 0.8)} title="Zoom out" style={zoomBtn}>&minus;</button>
              <input type="range" min={50} max={400} step={5} value={Math.min(400, Math.max(50, Math.round(zoom * 100)))} onChange={(e) => applyZoom(parseInt(e.target.value, 10) / 100)} style={{ width: 130, accentColor: '#FFD93D', cursor: 'pointer' }} />
              <button onClick={() => applyZoom(zoom * 1.25)} title="Zoom in" style={zoomBtn}>+</button>
              <span style={{ color: '#aaa', fontSize: 11, minWidth: 36, textAlign: 'center' }}>{Math.round(zoom * 100)}%</span>
              <button onClick={fitScreen} title="Fit page to screen" style={{ ...zoomBtn, width: 'auto', padding: '0 8px', fontSize: 11 }}>Fit</button>
            </div>
          )}
          </div>

          {/* RIGHT on desktop / BOTTOM sheet on mobile: settings */}
          <div style={{ ...(isMobile
            ? { width: 'auto', maxHeight: '45vh', borderTop: '1px solid #222' }
            : { width: 300, borderLeft: '1px solid #222' }),
            flexShrink: 0, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
            <div style={{ padding: 18, overflow: 'auto', display: 'flex', flexDirection: 'column', gap: 16, flex: 1 }}>
              <div>
                <div style={{ color: '#aaa', fontSize: 11, marginBottom: 6 }}>Shape</div>
                <div style={{ display: 'flex', gap: 6 }}>
                  <button style={{ ...seg(shape === 'rectangle'), flex: 1, justifyContent: 'center' }} onClick={() => toggleShape('rectangle')}><Square size={14} /> Rectangle</button>
                  <button style={{ ...seg(shape === 'trapezoid'), flex: 1, justifyContent: 'center' }} onClick={() => toggleShape('trapezoid')}><Hexagon size={14} /> Trapezoid</button>
                </div>
                {confirmRect && (
                  <div style={{ marginTop: 8, background: '#241c00', border: '1px solid #6b5800', borderRadius: 8, padding: 10 }}>
                    <div style={{ color: '#FFD93D', fontSize: 12, fontWeight: 600, marginBottom: 2 }}>Convert to rectangle?</div>
                    <div style={{ color: '#bbb', fontSize: 11, lineHeight: 1.5, marginBottom: 8 }}>This flattens the trapezoid (unequal sides are lost). The shape stays trapezoid until you confirm.</div>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <button onClick={() => setConfirmRect(false)} style={{ flex: 1, padding: '6px 10px', borderRadius: 6, border: '1px solid #333', background: 'transparent', color: '#aaa', cursor: 'pointer', fontSize: 12 }}>Keep trapezoid</button>
                      <button onClick={() => doConvertShape('rectangle')} style={{ flex: 1, padding: '6px 10px', borderRadius: 6, border: 'none', background: '#FFD93D', color: '#1a1a1a', cursor: 'pointer', fontSize: 12, fontWeight: 700 }}>Convert</button>
                    </div>
                  </div>
                )}
              </div>

              <div>
                <div style={{ color: '#aaa', fontSize: 11, marginBottom: 6 }}>Dimensions</div>
                {shape === 'rectangle' ? (
                  <div style={{ display: 'flex', gap: 12 }}>
                    {numInput('Width (N & S)', 'n', dims.n)}
                    {numInput('Height (E & W)', 'e', dims.e)}
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                    <div style={{ display: 'flex', gap: 12 }}>{numInput('North', 'n', dims.n)}{numInput('South', 's', dims.s)}</div>
                    <div style={{ display: 'flex', gap: 12 }}>{numInput('East', 'e', dims.e)}{numInput('West', 'w', dims.w)}</div>
                  </div>
                )}
              </div>

              {shape === 'trapezoid' && (
                <div>
                  <div style={{ color: '#aaa', fontSize: 11, marginBottom: 6 }}>Fixed corner (right-angle axis)</div>
                  <select value={origin} onChange={(e) => { const v = e.target.value; setOrigin(v); const w: any = getIframe()?.contentWindow; w?.postMessage({ type: 'SET_FIELDS', fields: { plot_origin: v } }, '*'); setTimeout(snapshot, 80); }}
                    style={{ width: '100%', background: '#1a1a1a', border: '1px solid #333', color: '#fff', borderRadius: 6, padding: '7px 9px', fontSize: 12 }}>
                    <option value="SW">Bottom-Left (S + W square)</option>
                    <option value="SE">Bottom-Right (S + E square)</option>
                    <option value="NW">Top-Left (N + W square)</option>
                    <option value="NE">Top-Right (N + E square)</option>
                  </select>
                  <div style={{ color: '#666', fontSize: 10, marginTop: 4 }}>The two walls at this corner stay perpendicular; the opposite corner floats.</div>
                </div>
              )}

              {/* Compact toggles */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {([
                  ['Show plot name', showName, toggleName],
                  ['Dimensions outside', dimsOutside, () => toggleDimsOutside(!dimsOutside)],
                ] as const).map(([label, on, fn], idx) => (
                  <div key={idx} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ color: '#bbb', fontSize: 12 }}>{label}</span>
                    <button onClick={fn as any} title={label}
                      style={{ width: 38, height: 20, borderRadius: 10, border: 'none', cursor: 'pointer', background: on ? '#FFD93D' : '#3a3a3a', position: 'relative', padding: 0, flexShrink: 0 }}>
                      <span style={{ position: 'absolute', top: 2, left: on ? 20 : 2, width: 16, height: 16, borderRadius: 8, background: '#fff', transition: 'left .12s' }} />
                    </button>
                  </div>
                ))}
              </div>

              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                  <span style={{ color: '#aaa', fontSize: 11 }}>Total area {areaOverride ? '(override)' : '(calculated)'}</span>
                  <button onClick={toggleAreaOverride} title="Override area"
                    style={{ width: 38, height: 20, borderRadius: 10, border: 'none', cursor: 'pointer', background: areaOverride ? '#FFD93D' : '#3a3a3a', position: 'relative', padding: 0, flexShrink: 0 }}>
                    <span style={{ position: 'absolute', top: 2, left: areaOverride ? 20 : 2, width: 16, height: 16, borderRadius: 8, background: '#fff', transition: 'left .12s' }} />
                  </button>
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <label style={{ display: 'flex', flexDirection: 'column', gap: 3, flex: 1 }}>
                    <span style={{ color: '#888', fontSize: 10 }}>Sq.Yards</span>
                    <input value={areaOverride ? areaYd : liveArea.yd} disabled={!areaOverride}
                      onChange={(e) => setAreaYd(e.target.value)} onBlur={() => setArea('area_sqyd_override', areaYd)}
                      onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }}
                      style={{ width: '100%', boxSizing: 'border-box', background: areaOverride ? '#1a1a1a' : '#141414', border: '1px solid #333', color: areaOverride ? '#fff' : '#888', borderRadius: 6, padding: '7px 9px', fontSize: 13 }} />
                  </label>
                  <label style={{ display: 'flex', flexDirection: 'column', gap: 3, flex: 1 }}>
                    <span style={{ color: '#888', fontSize: 10 }}>Sq.Mtrs</span>
                    <input value={areaOverride ? areaMt : liveArea.mt} disabled={!areaOverride}
                      onChange={(e) => setAreaMt(e.target.value)} onBlur={() => setArea('area_sqm_override', areaMt)}
                      onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }}
                      style={{ width: '100%', boxSizing: 'border-box', background: areaOverride ? '#1a1a1a' : '#141414', border: '1px solid #333', color: areaOverride ? '#fff' : '#888', borderRadius: 6, padding: '7px 9px', fontSize: 13 }} />
                  </label>
                </div>
                <div style={{ color: '#666', fontSize: 10, marginTop: 4 }}>{areaOverride ? 'Editing overrides the calculated area.' : 'Calculated automatically — toggle to override.'}</div>
              </div>

              <div>
                <div style={{ color: '#aaa', fontSize: 11, marginBottom: 6 }}>Roads</div>
                <div style={{ display: 'flex', gap: 6 }}>
                  {([['north', 'N'], ['south', 'S'], ['east', 'E'], ['west', 'W']] as const).map(([side, l]) => (
                    <button key={side} title={`Road on ${side} side`} style={{ ...seg(roads[side]), flex: 1, justifyContent: 'center' }} onClick={() => toggleRoad(side)}>{l}</button>
                  ))}
                </div>
              </div>

              <div style={{ color: '#666', fontSize: 11, lineHeight: 1.5 }}>
                Click an element to select it — the <b style={{ color: '#999' }}>plot</b> shows reshape corners, a <b style={{ color: '#999' }}>label/dimension</b> shows a move outline. Drag to move, double-click text to edit, or type exact sizes above. Changes apply live.
              </div>
              <div style={{ color: '#666', fontSize: 11, lineHeight: 1.6, borderTop: '1px solid #222', paddingTop: 10 }}>
                <div style={{ color: '#aaa', fontWeight: 700, marginBottom: 4 }}>Road (click to select)</div>
                <span style={{ color: '#e67e22' }}>●</span> width (far line) &nbsp; <span style={{ color: '#2f7bff' }}>●</span> end dots — drag to extend / rotate into an L<br />
                <span style={{ color: '#FFD93D' }}>●</span> pivot (off-centre corner) — drag to move, double-click to straighten<br />
                Near line stays pinned. <span style={{ color: '#9b59b6' }}>Pulse</span>: dot = move, ring = extend, <span style={{ color: '#16a085' }}>grip</span> = rotate, double-click = delete; <span style={{ color: '#2ecc71' }}>+</span> = add.
              </div>
            </div>

            <div style={{ display: 'flex', gap: 10, padding: '12px 18px', borderTop: '1px solid #222', flexShrink: 0 }}>
              <button onClick={cancel} style={{ flex: 1, padding: '9px 14px', borderRadius: 8, border: '1px solid #333', background: 'transparent', color: '#aaa', cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>Cancel</button>
              <button onClick={onClose} style={{ flex: 1, padding: '9px 14px', borderRadius: 8, border: 'none', background: '#FFD93D', color: '#1a1a1a', cursor: 'pointer', fontSize: 13, fontWeight: 700 }}>Apply</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BoundaryFigureEditor;
